import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoImg from 'src/assets/images/logo.png';
import { SidebarTrigger } from 'src/components/ui/sidebar';
import { Breadcrumb } from 'src/components/common/Breadcrumb';
import { ThemeToggle } from 'src/components/common/ThemeToggle';
import { getToken, getUser } from 'src/utils/authStorage';

// Route to label mapping for breadcrumbs
const routeLabels = {
  '/openings': 'Open Positions',
  '/change-password': 'Change Password',
  '/resume-preview': 'My Profile',
  '/profile-dashboard': 'My Profile',
  '/my-skills': 'My Skills',
  '/my-applications': 'My Applications',
  '/activate-account': 'Activate Account',
};

// Function to generate breadcrumb items from pathname
const generateBreadcrumbItems = (pathname) => {
  const items = [];

  if (pathname.startsWith('/openings')) {
    items.push({ label: 'Open Positions', href: '/openings' });
    const openingIdMatch = pathname.match(/^\/openings\/(.+)$/);
    if (openingIdMatch) {
      // Placeholder resolved at render time if dynamic breadcrumbs are provided
      items.push({ label: 'Details', href: pathname });
    }
  } else if (pathname.startsWith('/my-applications')) {
    items.push({ label: 'My Applications', href: '/my-applications' });
    // SeniorLevelFlow / InterviewInvite replace this placeholder with the real
    // role title via outletContext.setHeaderBreadcrumbs once their data loads;
    // this is what briefly shows on first paint, before that effect commits.
    const detailMatch = pathname.match(
      /^\/my-applications\/(requests|interviews)\/(.+)$/
    );
    if (detailMatch) {
      items.push({ label: 'Details', href: pathname });
    }
  } else {
    const route = Object.keys(routeLabels).find((r) => pathname.startsWith(r));
    if (route) {
      items.push({ label: routeLabels[route], href: pathname });
    }
  }

  return items;
};

export default function Header({
  showSidebarTrigger = false,
  showBranding = true,
  useContentContainer = false,
  contentPaddingClassName = 'px-2 lg:px-4',
  /** Optional right-side slot (e.g. mobile-only actions from route via MainLayout outlet context) */
  endContent = null,
  /** Dynamic breadcrumb items pushed by child routes (overrides auto-generated items) */
  breadcrumbOverride = null,
}) {
  const location = useLocation();
  const breadcrumbItems = showSidebarTrigger
    ? (breadcrumbOverride ?? generateBreadcrumbItems(location.pathname))
    : [];

  // Logo target: an authenticated candidate goes straight to the dashboard;
  // everyone else goes to the landing/root (which routes them appropriately).
  const logoUser = getUser();
  const logoTo =
    getToken() && logoUser?.roles?.includes('candidate')
      ? '/profile-dashboard'
      : '/';

  const contentClasses = useContentContainer
    ? 'mx-auto flex h-full w-full max-w-6xl items-center justify-between px-7'
    : `flex h-full w-full items-center justify-between ${contentPaddingClassName}`;

  return (
    <header
      className={`h-14 shrink-0 z-9999 ${showSidebarTrigger ? 'border-b border-[var(--border-light)] bg-[var(--surface)]' : 'bg-header-gradient'}`}
    >
      <div className={contentClasses}>
        <div className="flex min-w-0 flex-1 items-center gap-4">
          {showSidebarTrigger && (
            <SidebarTrigger className="md:hidden text-[var(--text-faint)] hover:bg-[var(--primary-light)] hover:text-[var(--primary)]" />
          )}
          {showSidebarTrigger && breadcrumbItems.length > 0 && (
            <Breadcrumb items={breadcrumbItems} className="py-0" />
          )}
          {showBranding && (
            <Link to={logoTo} className="flex items-center gap-2">
              <img src={logoImg} alt="AHEDGE Logo" className="h-6 w-auto" />
              <span className="text-base font-semibold tracking-wide text-white md:text-lg">
                AHEDGE
              </span>
            </Link>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <ThemeToggle
            className={
              showSidebarTrigger
                ? undefined
                : // On the branded gradient header (no sidebar), the default
                  // border-[var(--border)] bg-[var(--surface)] styling resolves
                  // to a plain white square with no border definition against
                  // the blue gradient — it reads as a different component
                  // entirely from the subtle toggle used on sidebar pages.
                  // Frosted-glass styling keeps it visually quiet here too,
                  // echoing the logo mark's own rgba(255,255,255,.15) treatment.
                  'border-white/20 bg-white/15 text-white backdrop-blur-sm hover:border-white/40 hover:bg-white/25 hover:text-white'
            }
          />
          {endContent}
        </div>
      </div>
    </header>
  );
}
